// modules/auth.js
// Authentication: login, 2FA, token storage, session check.
// No imports from other add-in modules — keeps dep graph clean.

const AUTH_API_URL = process.env.AUTH_API_URL || "";
const TOKEN_KEY = "miai_auth_token";
const EXPIRY_BUFFER = 5 * 60; // 5 min in seconds
const MAX_2FA_ATTEMPTS = 5;

// Module-scoped — NOT on window, not accessible from outside this module
let _tempToken = null;
let _twoFAAttempts = 0;

// ── Token helpers ─────────────────────────────────────────────────────────────

function _decodeJwt(token) {
  try {
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

function _storeAuth(data) {
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn("[AUTH] localStorage write:", e);
  }
}

function _loadAuth() {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export function getValidToken() {
  const auth = _loadAuth();
  if (!auth?.access_token) return null;
  const decoded = _decodeJwt(auth.access_token);
  if (!decoded) return null;
  const now = Math.floor(Date.now() / 1000);
  if (decoded.exp <= now + EXPIRY_BUFFER) {
    clearAuth();
    return null;
  }
  return auth.access_token;
}

export function getCurrentUser() {
  return _loadAuth()?.user || null;
}

export async function refreshUserProfile() {
  const token = getValidToken();
  if (!token) return null;
  const user = await _fetchProfile(token);
  if (user) {
    const auth = _loadAuth();
    if (auth) _storeAuth({ ...auth, user });
    return user;
  }
  return null;
}

// ── UI helpers ────────────────────────────────────────────────────────────────

export function showLoginScreen() {
  _show("auth-panel");
  _hide("twofa-panel");
  _hide("app-body");
  _setErr("auth-error", "");
  _setErr("twofa-error", "");
  const pw = document.getElementById("auth-password");
  if (pw) pw.value = "";
  setTimeout(() => document.getElementById("auth-email")?.focus(), 100);
}

export function showTwoFAScreen() {
  _hide("auth-panel");
  _show("twofa-panel");
  _setErr("twofa-error", "");
  setTimeout(() => document.getElementById("twofa-code-input")?.focus(), 100);
}

export function showMainPanel() {
  _hide("auth-panel");
  _hide("twofa-panel");
  _show("app-body");
  const user = getCurrentUser();
  const strip = document.getElementById("header-user-strip");
  const nameEl = document.getElementById("header-user-name");
  if (strip) strip.style.display = "flex";
  if (nameEl && user) {
    nameEl.textContent = user.full_name || user.email || "";
  }
}

function _show(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.style.display = id === "app-body" ? "flex" : "flex";
}

function _hide(id) {
  const el = document.getElementById(id);
  if (el) el.style.display = "none";
}

function _humanizeMsg(msg) {
  return msg.replace(/(\d+)\s*seconds?/gi, (_, s) => {
    const n = parseInt(s, 10);
    if (n >= 3600) {
      const h = Math.ceil(n / 3600);
      return `${h} hour${h > 1 ? "s" : ""}`;
    }
    if (n >= 60) {
      const m = Math.ceil(n / 60);
      return `${m} minute${m > 1 ? "s" : ""}`;
    }
    return `${n} second${n !== 1 ? "s" : ""}`;
  });
}

function _setErr(id, msg) {
  const el = document.getElementById(id);
  if (!el) return;
  el.textContent = msg;
  el.style.display = msg ? "block" : "none";
}

function _setBtnLoading(id, loading, loadText, defaultText) {
  const btn = document.getElementById(id);
  if (!btn) return;
  btn.disabled = loading;
  btn.textContent = loading ? loadText : defaultText;
}

// ── Auth API ──────────────────────────────────────────────────────────────────

async function _authFetch(path, body) {
  let res;
  try {
    res = await fetch(AUTH_API_URL + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error("Network error. Please check your connection.");
  }
  let data = {};
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const detail = data?.detail || data?.message;
    const msg =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
        ? detail.map((d) => d.msg).join("; ")
        : `Error ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

async function _fetchProfile(token) {
  try {
    const res = await fetch(AUTH_API_URL + "/users/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

async function _onLoginSuccess(accessToken) {
  const user = await _fetchProfile(accessToken);
  const decoded = _decodeJwt(accessToken);
  _storeAuth({
    access_token: accessToken,
    token_type: "bearer",
    expires_at: decoded?.exp ? decoded.exp * 1000 : null,
    user,
  });
  showMainPanel();
  window.dispatchEvent(new CustomEvent("miai:authenticated"));
}

// ── Check auth on load ────────────────────────────────────────────────────────

export async function checkAuth() {
  const token = getValidToken();
  if (!token) {
    showLoginScreen();
    return false;
  }
  showMainPanel();
  window.dispatchEvent(new CustomEvent("miai:authenticated"));
  return true;
}

// ── Login ─────────────────────────────────────────────────────────────────────

export async function handleLogin() {
  const email = (document.getElementById("auth-email")?.value || "").trim();
  const password = document.getElementById("auth-password")?.value || "";
  if (!email || !password) {
    _setErr("auth-error", "Please enter your email and password.");
    return;
  }
  _setBtnLoading("signin-btn", true, "Signing in…", "Sign In");
  _setErr("auth-error", "");
  try {
    const data = await _authFetch("/auth/login", { email, password });
    if (data.requires_2fa) {
      _tempToken = data.temp_token;
      _twoFAAttempts = 0;
      _setBtnLoading("signin-btn", false, "", "Sign In");
      showTwoFAScreen();
      _startTwoFATimer();
    } else {
      await _onLoginSuccess(data.access_token);
    }
  } catch (err) {
    _setErr("auth-error", err.message || "Login failed. Please check your credentials.");
    _setBtnLoading("signin-btn", false, "", "Sign In");
  }
}

// ── 2FA ───────────────────────────────────────────────────────────────────────

let _timerInterval = null;

function _startTwoFATimer() {
  _clearTwoFATimer();
  let secs = 5 * 60;
  const label = document.getElementById("twofa-timer");
  _timerInterval = setInterval(() => {
    secs--;
    if (secs <= 0) {
      _clearTwoFASession();
      _setErr("twofa-error", "Session expired. Please sign in again.");
      const btn = document.getElementById("twofa-submit-btn");
      if (btn) btn.disabled = true;
      return;
    }
    if (label && secs <= 60) {
      label.textContent = `Expires in ${secs}s`;
      label.className = "twofa-timer warning";
    }
  }, 1000);
}

function _clearTwoFATimer() {
  if (_timerInterval) {
    clearInterval(_timerInterval);
    _timerInterval = null;
  }
}

function _clearTwoFASession() {
  _tempToken = null;
  _twoFAAttempts = 0;
  _clearTwoFATimer();
}

export async function handleTwoFA() {
  const panel = document.getElementById("twofa-panel");
  const useBackup = panel?.dataset?.mode === "backup";
  const inputId = useBackup ? "twofa-backup-input" : "twofa-code-input";
  let code = (document.getElementById(inputId)?.value || "").trim().replace(/\s/g, "");

  if (!code) {
    _setErr("twofa-error", "Please enter the verification code.");
    return;
  }

  // Format validation
  if (!useBackup && !/^\d{6}$/.test(code)) {
    _setErr("twofa-error", "Authenticator code must be 6 digits.");
    return;
  }
  if (useBackup) {
    // Normalize: strip dashes, uppercase, then re-add dash at position 4
    const stripped = code.replace(/-/g, "").toUpperCase();
    if (!/^[A-Z0-9]{8}$/.test(stripped)) {
      _setErr("twofa-error", "Invalid backup code. Please check and try again.");
      return;
    }
    code = stripped.slice(0, 4) + "-" + stripped.slice(4);
  }

  if (!_tempToken) {
    _setErr("twofa-error", "Session expired. Please sign in again.");
    showLoginScreen();
    return;
  }

  // Client-side attempt lockout
  if (_twoFAAttempts >= MAX_2FA_ATTEMPTS) {
    _setErr("twofa-error", "Too many failed attempts. Please sign in again.");
    _clearTwoFASession();
    showLoginScreen();
    return;
  }

  _setBtnLoading("twofa-submit-btn", true, "Verifying…", "Verify");
  _setErr("twofa-error", "");
  try {
    const data = await _authFetch("/auth/2fa/verify", {
      code,
      temp_token: _tempToken,
      use_backup_code: useBackup,
    });
    _clearTwoFASession();
    await _onLoginSuccess(data.access_token);
  } catch (err) {
    const msg = _humanizeMsg(err.message || "Invalid code. Please try again.");
    const isRateLimited = msg.toLowerCase().includes("too many");
    if (!isRateLimited) _twoFAAttempts++;
    const remaining = MAX_2FA_ATTEMPTS - _twoFAAttempts;
    const suffix = !isRateLimited && remaining > 0 ? ` (${remaining} attempt${remaining === 1 ? "" : "s"} left)` : "";
    _setErr("twofa-error", msg + suffix);
    _setBtnLoading("twofa-submit-btn", false, "", "Verify");
    if (_twoFAAttempts >= MAX_2FA_ATTEMPTS) {
      _setErr("twofa-error", "Too many failed attempts. Please sign in again.");
      _clearTwoFASession();
      showLoginScreen();
    }
  }
}

export function toggleBackupMode() {
  const panel = document.getElementById("twofa-panel");
  const isBackup = panel?.dataset?.mode === "backup";
  const newMode = isBackup ? "totp" : "backup";
  if (panel) panel.dataset.mode = newMode;

  const totpGroup = document.getElementById("twofa-totp-group");
  const backupGroup = document.getElementById("twofa-backup-group");
  if (totpGroup) totpGroup.style.display = newMode === "totp" ? "flex" : "none";
  if (backupGroup) backupGroup.style.display = newMode === "backup" ? "flex" : "none";

  const toggleBtn = document.getElementById("twofa-mode-toggle");
  if (toggleBtn) {
    toggleBtn.textContent =
      newMode === "totp" ? "Use backup code instead" : "Use authenticator app instead";
  }
  const hint = document.getElementById("twofa-hint");
  if (hint) {
    hint.textContent =
      newMode === "backup"
        ? "Enter one of your backup codes."
        : "Enter the 6-digit code from your authenticator app.";
  }
  _setErr("twofa-error", "");
  setTimeout(() => {
    document.getElementById(newMode === "backup" ? "twofa-backup-input" : "twofa-code-input")?.focus();
  }, 50);
}

// ── Logout ────────────────────────────────────────────────────────────────────

export function handleLogout() {
  clearAuth();
  _clearTwoFASession();
  // Reload the add-in to cleanly reset all document state
  window.location.reload();
}
